import { Outlet, NavLink, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { House, MagnifyingGlass, CalendarBlank, User, Bell, Briefcase, Users, ClipboardText, SignOut, Buildings, FileText, ChartBar, ChatCircle, Lifebuoy, Bank, Gift, Broadcast, Receipt, GraduationCap, ArrowsClockwise, Heart, ShieldCheck, ClockCounterClockwise, Lightning, SlidersHorizontal } from "@phosphor-icons/react";
import { Link } from "react-router-dom";
import LangSwitcher from "@/components/LangSwitcher";
import VerifyEmailBanner from "@/components/VerifyEmailBanner";

const staffNav = [
  { to: "/staff", label: "Home", icon: House, exact: true },
  { to: "/staff/jobs", label: "Find Jobs", icon: MagnifyingGlass },
  { to: "/staff/offers", label: "Live Offers", icon: Lightning },
  { to: "/staff/bookings", label: "Bookings", icon: CalendarBlank },
  { to: "/staff/profile", label: "Profile", icon: User },
];

const companyNav = [
  { to: "/company", label: "Dashboard", icon: ChartBar, exact: true },
  { to: "/company/jobs", label: "Jobs", icon: Briefcase },
  { to: "/company/bookings", label: "Bookings", icon: ClipboardText },
  { to: "/company/messages", label: "Messages", icon: ChatCircle, badge: "unread" },
  { to: "/company/support", label: "Support", icon: Lifebuoy },
  { to: "/company/notifications", label: "Alerts", icon: Bell },
  { to: "/company/profile", label: "Profile", icon: Buildings },
];

const staffMoreNav = [
  { to: "/staff/availability", label: "Availability", icon: CalendarBlank },
  { to: "/staff/documents", label: "Documents", icon: FileText },
  { to: "/staff/skill-tests", label: "Skill Tests", icon: GraduationCap },
  { to: "/staff/swaps", label: "Swaps", icon: ArrowsClockwise },
  { to: "/staff/messages", label: "Messages", icon: ChatCircle, badge: "unread" },
  { to: "/staff/payouts", label: "Payouts", icon: Bank },
  { to: "/staff/referrals", label: "Referrals", icon: Gift },
  { to: "/staff/sessions", label: "Sessions", icon: ShieldCheck },
  { to: "/staff/support", label: "Support", icon: Lifebuoy },
];

const companyMoreNav = [
  { to: "/company/team", label: "Team", icon: Users },
  { to: "/company/favourites", label: "Favourites", icon: Heart },
  { to: "/company/invoices", label: "Invoices", icon: Receipt },
  { to: "/company/referrals", label: "Referrals", icon: Gift },
  { to: "/company/sessions", label: "Sessions", icon: ShieldCheck },
];

const adminNav = [
  { to: "/admin", label: "Overview", icon: ChartBar, exact: true },
  { to: "/admin/dispatch", label: "Live Dispatch", icon: Broadcast },
  { to: "/admin/matching", label: "Matching", icon: SlidersHorizontal },
  { to: "/admin/live", label: "Live Ops", icon: Broadcast },
  { to: "/admin/users", label: "Users", icon: Users },
  { to: "/admin/jobs", label: "Jobs", icon: Briefcase },
  { to: "/admin/bookings", label: "Bookings", icon: ClipboardText },
  { to: "/admin/documents", label: "Documents", icon: FileText },
  { to: "/admin/audit", label: "Audit Log", icon: ClockCounterClockwise },
  { to: "/admin/sessions", label: "Sessions", icon: ShieldCheck },
  { to: "/admin/support", label: "Support", icon: Lifebuoy },
  { to: "/admin/rate-limits", label: "Rate Limits", icon: ShieldCheck },
];

const NAV_BY_ROLE = { staff: staffNav, company: companyNav, admin: adminNav };

export default function AppShell() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const role = user?.role;
  const items = NAV_BY_ROLE[role] || adminNav;
  const isStaff = role === "staff";
  const [unread, setUnread] = useState(0);

  useEffect(() => {
    let cancelled = false;
    const poll = async () => {
      try {
        const r = await api.get("/messages/unread-count");
        if (!cancelled) setUnread(r.data.count || 0);
      } catch (err) { if (!cancelled) console.warn("unread poll failed", err); }
    };
    poll();
    const id = setInterval(poll, 8000);
    return () => { cancelled = true; clearInterval(id); };
  }, []);

  const handleLogout = async () => { await logout(); nav("/login"); };

  return (
    <div className="min-h-screen bg-[#F8FAFC] flex">
      {!isStaff && (
        <aside className="hidden md:flex w-64 bg-white border-r border-slate-200 flex-col p-6 sticky top-0 h-screen">
          <div className="mb-8">
            <div className="font-display text-2xl font-extrabold tracking-tight">ShiftFlex</div>
            <div className="label-caps mt-1">{role} console</div>
          </div>
          <nav className="flex-1 flex flex-col gap-1">
            {[...items, ...(role === "company" ? companyMoreNav : [])].map((it) => (
              <NavLink key={it.to} to={it.to} end={it.exact}
                data-testid={`nav-${it.label.toLowerCase().replace(/\s/g,'-')}`}
                className={({ isActive }) => `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-semibold transition-colors ${isActive ? "bg-blue-600 text-white" : "text-slate-700 hover:bg-slate-100"}`}>
                <div className="relative"><it.icon size={18} />{it.badge === "unread" && unread > 0 && <span className="absolute -top-1 -right-1 min-w-[16px] h-4 px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center">{unread > 9 ? "9+" : unread}</span>}</div>
                {it.label}
              </NavLink>
            ))}
          </nav>
          <button data-testid="logout-btn" onClick={handleLogout} className="flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-semibold text-slate-700 hover:bg-slate-100">
            <SignOut size={18} /> Log out
          </button>
          <div className="mt-3 pt-3 border-t border-slate-200"><LangSwitcher /></div>
        </aside>
      )}

      {isStaff && (
        <aside className="hidden md:flex w-56 bg-white border-r border-slate-200 flex-col p-6 sticky top-0 h-screen">
          <div className="mb-8">
            <div className="font-display text-2xl font-extrabold tracking-tight">ShiftFlex</div>
            <div className="label-caps mt-1">staff console</div>
          </div>
          <nav className="flex-1 flex flex-col gap-1">
            {[...staffNav, ...staffMoreNav].map((it) => (
              <NavLink key={it.to} to={it.to} end={it.exact}
                data-testid={`nav-${it.label.toLowerCase().replace(/\s/g,'-')}`}
                className={({ isActive }) => `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-semibold transition-colors ${isActive ? "bg-blue-600 text-white" : "text-slate-700 hover:bg-slate-100"}`}>
                <div className="relative"><it.icon size={18} />{it.badge === "unread" && unread > 0 && <span className="absolute -top-1 -right-1 min-w-[16px] h-4 px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center">{unread > 9 ? "9+" : unread}</span>}</div>
                {it.label}
              </NavLink>
            ))}
          </nav>
          <button data-testid="logout-btn" onClick={handleLogout} className="flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-semibold text-slate-700 hover:bg-slate-100">
            <SignOut size={18} /> Log out
          </button>
        </aside>
      )}

      <main className="flex-1 min-w-0 pb-24 md:pb-6">
        <VerifyEmailBanner />
        <div className="md:hidden sticky top-0 z-40 bg-white/85 backdrop-blur-xl border-b border-slate-200 px-4 py-3 flex items-center justify-between">
          <div className="font-display text-xl font-extrabold">ShiftFlex</div>
          <div className="flex items-center gap-3">
            {isStaff && <Link to="/staff/notifications" data-testid="top-alerts-link" className="text-slate-700"><Bell size={22} /></Link>}
            <LangSwitcher compact />
            <button data-testid="logout-btn-mobile" onClick={handleLogout} className="text-slate-600 text-sm font-semibold">Log out</button>
          </div>
        </div>
        <div className="px-4 sm:px-6 md:px-8 py-6 max-w-7xl mx-auto">
          <Outlet />
        </div>
      </main>

      {isStaff && (
        <nav className="glass-nav md:hidden fixed bottom-0 left-0 right-0 z-50 h-16 flex items-stretch justify-around">
          {items.map((it) => (
            <NavLink key={it.to} to={it.to} end={it.exact}
              data-testid={`tab-${it.label.toLowerCase().replace(/\s/g,'-')}`}
              className={({ isActive }) => `flex-1 flex flex-col items-center justify-center gap-0.5 text-[11px] font-semibold ${isActive ? "text-blue-600" : "text-slate-600"}`}>
              <div className="relative"><it.icon size={22} />{it.badge === "unread" && unread > 0 && <span className="absolute -top-1 -right-1 min-w-[16px] h-4 px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center">{unread > 9 ? "9+" : unread}</span>}</div>
              {it.label}
            </NavLink>
          ))}
        </nav>
      )}
    </div>
  );
}
